import * as React from "react";

import {Customer} from "../../../../domain/model/customer";
import {TJobCareerWithCustomerID} from "../../../../domain/repository/job_career_repository";

import {
    createMultiSkillTagAPI,
    createOccupationSkillTagAPI,
    createSingleSkillTagAPI,
} from "src/infra/api/service/skill_tag_api";
import styled from "styled-components";
import {routeConfig} from "../../../../config/language/ja-JP/presentation/route_text";
import {ScoutCustomersEmpty} from "../../../../config/view_const/scout_customers";
import {ClientBookmark} from "../../../../domain/model/client_bookmark";
import {Pager} from "../../../../domain/model/common";
import {MultiSkillTag} from "../../../../domain/model/skill_tag";
import {
    IMultiSkillTagRepository,
    IOccupationSkillTagRepository,
    ISingleSkillTagRepository,
} from "../../../../domain/repository/skill_tag_repository";
import {createApiClient, IApiClient} from "../../../../infra/api/client";
import {entity} from "../../../../infra/api/rpc/api";
import PageWrapper from "../../../common/atom/wrapper/page_wrapper";
import Breadcrumbs from "../../../common/molecular/breadcrumbs/breadcrumbs";
import EmptyData from "../../../common/organism/empty_data";
import Progress from "../../../common/organism/progress";
import {TBreadcrumbItem, TPagination} from "../../../type/common";
import ClientScoutCustomerCommonlySearchedWords from "./client_scout_customers_commonly_searched_words";
import ClientScoutCustomersMain from "./client_scout_customers_main";
import ClientScoutCustomersNarrowDownForm from "./client_scout_customers_narrow_down_form";
import Faq from "../../faq";

const form = "ClientScoutCustomerListSearchForm";

interface IProps {
    isInit: boolean;
    isLoading: boolean;
    isSearch: boolean;
    customers: Customer[];
    customerCount: number;
    hasSearchQuery: boolean;
    jobCareersWithCustomerID: TJobCareerWithCustomerID[];
    clientBookmarks: ClientBookmark[];
    searchFormInitValues: {
        q: string;
        occupationCategory: string;
        occupationParent: string;
        occupationSkillTag: string;
        singlePhaseTag: string;
        singleServiceClassificationTag: string;
        multiGyokaiTag: string;
        multiCXOTag: string;
        multiOverseaTag: string;
        multiOtherTag: string;
        multiSkillTag: string;
        multiSkillTagCategory: string;
        gender: string;
        age: string;
        workIntention: string;
        fullTimePossibility: string;
    };
    paginationPrams: TPagination;
    // handleSearch: (values: any) => void;
    handleDetailSearch: (values: any) => void;
    handleClickLink: (customer: Customer) => (event: any) => void;
    onResetSearch: () => void;
}

const apiClient: IApiClient = createApiClient();
const occupationSkillTagRepository: IOccupationSkillTagRepository =
    createOccupationSkillTagAPI(apiClient);
const singleSkillTagRepository: ISingleSkillTagRepository =
    createSingleSkillTagAPI(apiClient);
const multiSkillTagRepository: IMultiSkillTagRepository =
    createMultiSkillTagAPI(apiClient);

const ScoutCustomersComponent: React.FC<IProps> = (props) => {
    const {
        isInit,
        isLoading,
        isSearch,
        customers,
        customerCount,
        hasSearchQuery,
        jobCareersWithCustomerID,
        clientBookmarks,
        searchFormInitValues,
        paginationPrams,
        // handleSearch,
        handleClickLink,
        onResetSearch,
    } = props;

    const [occupationCategories, setOccupationCategories] = React.useState<
        entity.IOccupationSkillTagCategoryItem[]
    >([]);

    const [occupationParents, setOccupationParents] = React.useState<
        entity.IOccupationSkillTag[]
    >([]);

    const [occupationChildren, setOccupationChildren] = React.useState<
        entity.IOccupationSkillTag[]
    >([]);

    const [singlePhaseTags, setSinglePhaseTags] = React.useState<
        entity.ISingleSkillTag[]
    >([]);

    const [singleServiceClassificationTags, setSingleServiceClassificationTags] =
        React.useState<entity.ISingleSkillTag[]>([]);

    const [multiGyokaiTags, setMultiGyokaiTags] = React.useState<
        entity.IMultiSkillTag[]
    >([]);

    const [multiCXOTags, setMultiCXOTags] = React.useState<
        entity.IMultiSkillTag[]
    >([]);

    const [multiOverseaTags, setMultiOverseaTags] = React.useState<
        entity.IMultiSkillTag[]
    >([]);

    const [multiOtherTags, setMultiOtherTags] = React.useState<
        entity.IMultiSkillTag[]
    >([]);

    const [selectCategory, setSelectCategory] = React.useState<
        entity.OccupationSkillTag.Category | undefined
    >(undefined);

    const [selectParentId, setSelectParentId] = React.useState<
        string | undefined
    >(undefined);

    const [selectMultiSkillTag, setSelectMultiSkillTag] = React.useState<
        MultiSkillTag | undefined
    >(undefined);

    React.useEffect(() => {
        try {
            occupationSkillTagRepository.getAllCategory().then((items) => {
                setOccupationCategories(items);
            });

            singleSkillTagRepository
                .getAllByCategory(entity.SingleSkillTag.Category.Category_GoodPhase)
                .then((items) => {
                    setSinglePhaseTags(items);
                });

            singleSkillTagRepository
                .getAllByCategory(
                    entity.SingleSkillTag.Category.Category_GoodServiceClassification
                )
                .then((items) => {
                    setSingleServiceClassificationTags(items);
                });

            multiSkillTagRepository
                .getAllByCategory(
                    entity.MultiSkillTag.Category.Category_SpecialtyIndustry
                )
                .then((items) => {
                    setMultiGyokaiTags(items);
                });

            multiSkillTagRepository
                .getAllByCategory(entity.MultiSkillTag.Category.Category_CXO)
                .then((items) => {
                    setMultiCXOTags(items);
                });

            multiSkillTagRepository
                .getAllByCategory(
                    entity.MultiSkillTag.Category.Category_OverseasExpansion
                )
                .then((items) => {
                    setMultiOverseaTags(items);
                });

            multiSkillTagRepository
                .getAllByCategory(entity.MultiSkillTag.Category.Category_Other)
                .then((items) => {
                    setMultiOtherTags(items);
                });

            if (props.searchFormInitValues.occupationCategory) {
                const str =
                    entity.OccupationSkillTag.Category[
                        parseInt(props.searchFormInitValues.occupationCategory, undefined)
                        ];
                const category: entity.OccupationSkillTag.Category =
                    entity.OccupationSkillTag.Category[
                        str as keyof typeof entity.OccupationSkillTag.Category
                        ];
                occupationSkillTagRepository
                    .getAllByCategory(category)
                    .then((items) => {
                        setOccupationParents(items);
                    });
            } else {
                setOccupationParents([]);
            }

            if (props.searchFormInitValues.occupationParent) {
                occupationSkillTagRepository
                    .getAll({
                        id: props.searchFormInitValues.occupationParent,
                        pager: new Pager(1, 10000),
                    })
                    .then((items) => {
                        setOccupationChildren(items);
                    });
            } else {
                setOccupationChildren([]);
            }

            if (props.searchFormInitValues.multiSkillTag) {
                multiSkillTagRepository
                    .get(props.searchFormInitValues.multiSkillTag)
                    .then((item) => {
                        setSelectMultiSkillTag(item);
                    });
            }
        } catch (e) {

        }
    }, []);

    React.useEffect(() => {
        if (selectCategory) {
            occupationSkillTagRepository
                .getAllByCategory(selectCategory)
                .then((items) => {
                    setOccupationParents(items);
                    setOccupationChildren([]);
                });
        } else {
            setOccupationParents([]);
            setOccupationChildren([]);
        }
    }, [selectCategory]);

    React.useEffect(() => {
        if (selectParentId) {
            occupationSkillTagRepository
                .getAll({
                    id: selectParentId,
                    pager: new Pager(1, 10000),
                })
                .then((items) => {
                    setOccupationChildren(items);
                });
        } else {
            setOccupationChildren([]);
        }
    }, [selectParentId]);

    // 初期化
    if (isInit) {
        return <Progress canAnimation={false}/>;
    }

    const breadcrumbItems: TBreadcrumbItem[] = [routeConfig.clientRoot];

    return (
        <React.Fragment>
            {isLoading && <Progress canAnimation={false}/>}
            <PageWrapper>
                <Breadcrumbs breadcrumbItems={breadcrumbItems}/>
                {/*<PageTopInfo*/}
                {/*  information={pageTopInfoConfig.clientScout}*/}
                {/*  linkURL={OuterURL.clientScoutFaqURL}*/}
                {/*/>*/}
                <Faq title={"エキスパートの閲覧について"}
                     text={"エキスパートの閲覧は、新着順、課題別、職種別での表示が可能です。またフリーワード検索や、業界などの項目による詳細な絞り込み機能も利用できます。"}
                     link={
                         "https://branch-saturn-9ef.notion.site/180b91fe393e802aa54cd6e88aff7d1d"
                     }/>

                <ClientScoutCustomerCommonlySearchedWords/>
                {/* <ClientScoutCustomersSearchFormWrapper>
          <ClientScoutCustomersSearchForm
            form={form}
            onSubmit={handleSearch}
            initialValues={{
              ...searchFormInitValues,
              categories,
            }}
            enableReinitialize={true}
          />
        </ClientScoutCustomersSearchFormWrapper> */}

                <ClientScoutCustomersNarrowDownForm
                    form={form}
                    onSubmit={props.handleDetailSearch}
                    initialValues={{
                        ...searchFormInitValues,
                        occupationCategories,
                        occupationParents,
                        occupationChildren,
                        multiGyokaiTags,
                        multiCXOTags,
                        multiOverseaTags,
                        multiOtherTags,
                        singlePhaseTags,
                        singleServiceClassificationTags,
                    }}
                    handleOccupationSkillTagCategoryChange={(e) => {
                        const str =
                            entity.OccupationSkillTag.Category[
                                parseInt(e.target.value, undefined)
                                ];
                        const category: entity.OccupationSkillTag.Category =
                            entity.OccupationSkillTag.Category[
                                str as keyof typeof entity.OccupationSkillTag.Category
                                ];
                        setSelectCategory(category);
                        // handleOccupationSkillTagCategoryChange(e);
                    }}
                    handleOccupationSkillTagParentChange={(e) => {
                        setSelectParentId(e.target.value);
                        // handleOccupationSkillTagParentChange(e);
                    }}
                    handleOccupationSkillTagChildChange={(e) => {
                        // handleOccupationSkillTagChildChange(e);
                    }}
                    onResetSearch={() => {
                        setOccupationParents([]);
                        setOccupationChildren([]);

                        onResetSearch();
                    }}
                />

                {customerCount === 0 && (
                    <EmptyData
                        main={ScoutCustomersEmpty.scoutCustomersForClientMain}
                        description={ScoutCustomersEmpty.scoutCustomersForClientDescription}
                    />
                )}
                {customerCount > 0 && (
                    <ClientScoutCustomersMain
                        isSearch={isSearch}
                        customers={customers}
                        customerCount={customerCount}
                        hasSearchQuery={hasSearchQuery}
                        jobCareersWithCustomerID={jobCareersWithCustomerID}
                        clientBookmarks={clientBookmarks}
                        isLoading={isLoading}
                        paginationPrams={paginationPrams}
                        handleClickLink={handleClickLink}
                        multiSkillTag={selectMultiSkillTag}
                        query={searchFormInitValues.q}
                    />
                )}
            </PageWrapper>
        </React.Fragment>
    );
};

export default ScoutCustomersComponent;

const ClientScoutCustomersSearchFormWrapper = styled.div`
    margin-bottom: 40px;
`;



// WEBPACK FOOTER //
// ./src/presentation/component/client/scout_customers/client_scout_customers.tsx